import type { TransactionSimulation } from '@blockaid/client/resources/evm';

import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';

/**
 * Extract function name from a function signature
 * e.g., "supply(address,uint256,address,uint16)" -> "supply"
 */
function extractFunctionNameFromSignature(signature: string): string {
  const match = signature.match(/^([^(]+)/);
  return match ? match[1] : signature;
}

/**
 * Extract contract address from EVM transaction validation result
 */
export function getContractAddress(
  txnValidationResult: BlockaidValidationResult
): string {
  if (!txnValidationResult) return '';
  // Handle EVM transactions with nested simulation
  if ('simulation' in txnValidationResult && txnValidationResult.simulation) {
    const simulation = txnValidationResult.simulation;
    if ('params' in simulation && simulation.params) {
      return simulation.params.to || '';
    }
  }

  return '';
}

/**
 * Extract contract name from EVM transaction validation result
 */
export function getContractName(
  txnValidationResult: BlockaidValidationResult,
  contractAddress: string
): string {
  if (!contractAddress || !txnValidationResult) return '';

  // Handle EVM transactions with nested simulation
  if ('simulation' in txnValidationResult && txnValidationResult.simulation) {
    const simulation = txnValidationResult.simulation;

    // Check if it's a successful simulation (not an error)
    if (
      'status' in simulation &&
      simulation.status === 'Success' &&
      'address_details' in simulation
    ) {
      const successfulSim = simulation as TransactionSimulation;
      if (successfulSim.address_details && contractAddress) {
        const addressDetails = successfulSim.address_details[contractAddress];
        if (addressDetails) {
          return addressDetails.name_tag || addressDetails.contract_name || '';
        }
      }
    }
  }

  return '';
}

/**
 * Extract function name from EVM transaction validation result
 */
export function getFunctionName(
  txnValidationResult: BlockaidValidationResult
): string {
  if (!txnValidationResult) return '';

  // Handle EVM transactions with nested simulation
  if ('simulation' in txnValidationResult && txnValidationResult.simulation) {
    const simulation = txnValidationResult.simulation;

    // Check if it's a successful simulation (not an error)
    if (
      'status' in simulation &&
      simulation.status === 'Success' &&
      'params' in simulation
    ) {
      const successfulSim = simulation as TransactionSimulation;
      if (successfulSim.params?.calldata?.function_signature) {
        return extractFunctionNameFromSignature(
          successfulSim.params.calldata.function_signature
        );
      }
    }
  }

  // Handle direct TransactionSimulation structure
  if (
    'params' in txnValidationResult &&
    (txnValidationResult as any).params?.calldata?.function_signature
  ) {
    return extractFunctionNameFromSignature(
      (txnValidationResult as any).params.calldata.function_signature
    );
  }

  return '';
}

/**
 * Format function name for display (capitalize first letter)
 */
export function formatFunctionName(functionName: string): string {
  if (!functionName) return '';
  return functionName.charAt(0).toUpperCase() + functionName.slice(1);
}
